import { EXPRESSION_SOURCE } from "./design-fault-reporting.js";
import {
    applyNumberPrecision,
    configuredDecimals,
    normalizeComputed,
} from "./compute-value.js";
import { checkComputedValueType } from "./computed-type-check.js";
import {
    createExpressionContext,
    evaluateExpressionSafe,
} from "./expression-sandbox.js";
import { buildItemTypeMap, getDefaultItemValue, hasValue } from "./form-helpers.js";
import { isGroupGated, isHiddenAlways } from "./form-visibility.js";

// Unique sentinel passed as evaluateExpressionSafe's default so a thrown
// expression is distinguishable from one that legitimately evaluates to
// `undefined`. An expression can never produce this value, so `result ===
// EVAL_THREW` uniquely means "the expression threw".
const EVAL_THREW = Symbol("computation-threw");

/**
 * Builds the two lookups a recompute pass needs: oid to item, and oid to the
 * item group holding it.
 *
 * Both hold the live nodes, which updateVisibility mutates in place, so
 * `isVisible` read through them is always current. The hook memoises this on
 * `itemGroups` identity.
 *
 * @param {Array<{ oid: string, items?: Array }>} itemGroups
 * @returns {{ itemLookup: Map<string, object>, groupLookup: Map<string, object> }}
 */
export function buildItemLookups(itemGroups) {
    const items = new Map();
    const groups = new Map();
    for (const ig of itemGroups ?? []) {
        for (const item of ig.items ?? []) {
            items.set(item.oid, item);
            groups.set(item.oid, ig);
        }
    }
    return { itemLookup: items, groupLookup: groups };
}

/**
 * Walks the topo order and evaluates Computation items, skipping hidden ones
 * and (optionally) any OID not in `affectedOids`. Used by both the
 * initial-compute effect and the debounced reactive-recompute effect.
 *
 * This is `useFunctionRecompute`'s `runComputations`, lifted out unchanged so it
 * can run — and be tested — without a React component tree, and so it can live
 * in the engine half of the shared package. The hook keeps only the scheduling:
 * the 300 ms debounce, the latest-snapshot refs, change detection, and effect
 * firing.
 *
 * Everything the closure used to read is now an explicit parameter, and the
 * writes still go through injected callbacks in the original order. That is
 * deliberate. A returned plan would be tidier and would also reorder the
 * interleaved report/write/seed sequence that later items read back through
 * `context.formData` — the cascade depends on it. This move is meant to be
 * provably behaviour-neutral; elegance can come later.
 *
 * @param {{
 *   form: object,
 *   computedItems: Array<object>,
 *   topoOrder: string[],
 *   computedItemMap: Map<string, object>,
 *   itemLookup: Map<string, object>,
 *   groupLookup: Map<string, object>,
 *   formData: object,
 *   dependencies: object,
 *   identifierModel: object,
 *   affectedOids: Set<string> | null | undefined,
 *   setValue: (oid: string, value: *) => void,
 *   setItemValue: (oid: string, value: *, format?: *) => void,
 *   reportTypeMismatch: (mismatch: object) => void,
 *   resolveTypeMismatch: (oid: string) => void,
 * }} params
 */
export function runComputations({
    form,
    computedItems,
    topoOrder,
    computedItemMap,
    itemLookup,
    groupLookup,
    formData,
    dependencies,
    identifierModel,
    affectedOids,
    setValue,
    setItemValue,
    reportTypeMismatch,
    resolveTypeMismatch,
}) {
    if (!computedItems.length) return;

    const context = createExpressionContext({
        formData: formData ?? {},
        dependencies: dependencies ?? {},
        identifierModel: identifierModel ?? {},
        itemTypes: buildItemTypeMap(form),
    });

    // Represent empty computed values as `undefined` (not their stored empty
    // sentinel) so a cleared upstream computed propagates "no value" (→ NaN)
    // to dependents instead of coercing null/""/[] to 0 — e.g. a downstream
    // `C = B + 1` must not recompute as 1 once B is cleared. Only computed
    // OIDs are normalised (plain inputs keep their own empty semantics), and
    // this also keeps the cascade stable on the next debounce tick, where
    // the cleared value has already been persisted as its empty sentinel.
    const seededFormData = { ...context.formData };
    for (const computedOid of computedItemMap.keys()) {
        if (!hasValue(seededFormData[computedOid])) {
            seededFormData[computedOid] = undefined;
        }
    }
    context.formData = seededFormData;

    for (const oid of topoOrder) {
        if (affectedOids && !affectedOids.has(oid)) continue;

        const item = computedItemMap.get(oid);
        if (!item) continue;

        // Hidden items are skipped — except hide-always ones, which compute
        // in the background (see isHiddenAlways). Falsy, not === false: a
        // throwing visibility condition leaves isVisible undefined, and
        // rendering already treats that as hidden.
        const itemMeta = itemLookup.get(oid);
        if (!itemMeta?.isVisible && !isHiddenAlways(itemMeta)) {
            continue;
        }

        // A gated group produces no function value on the server either, and
        // updateVisibility has already cleared the stored one.
        const groupMeta = groupLookup.get(oid);
        if (isGroupGated(groupMeta)) {
            continue;
        }

        // Keep an expression failure distinct from a genuine undefined
        // result. A visible stored value survives a transient failure; a
        // hidden value cannot be inspected and is cleared below.
        const patientVisible = Boolean(
            itemMeta?.isVisible && groupMeta?.isVisible
        );
        const result = evaluateExpressionSafe(
            item.expression,
            context,
            EVAL_THREW,
            {
                source: EXPRESSION_SOURCE.FUNCTION,
                oid,
                label: itemMeta?.label,
            }
        );
        let value = result;
        if (result === EVAL_THREW) {
            if (patientVisible) {
                continue;
            }
            value = undefined;
        }

        // normalizeComputed maps NaN/±Infinity to undefined, so a non-finite
        // computation is not written into formData (nor seeded into chained
        // expressions).
        const normalized = normalizeComputed(value);

        // An invalid Date is the one case where the raw value says more
        // than the normalized one. normalizeComputed has to map it to
        // `undefined` (date-fns `format()` throws on it, and useDefaults
        // calls that with no gate behind it), which would otherwise read
        // here as an ordinary "computed nothing" and go unreported. Hand
        // the gate the raw Date instead so it is rejected and NAMED —
        // `describeValue` already words it "an invalid date".
        const isInvalidDate =
            value instanceof Date && Number.isNaN(value.getTime());
        const gateValue = isInvalidDate ? value : normalized;

        // Type gate. Checked AFTER normalizeComputed (bar the case above)
        // so its Date → string and non-finite → undefined conversions are
        // already applied and this only judges what would actually reach
        // the component.
        const typeCheck = checkComputedValueType(gateValue, itemMeta?.itemType);
        if (!typeCheck.ok) {
            reportTypeMismatch({
                oid,
                label: itemMeta?.label,
                itemType: itemMeta?.itemType,
                expression: item.expression,
                expected: typeCheck.expected,
                actual: typeCheck.actual,
            });

            // Store the item's empty value rather than the unusable one, on
            // both the initial and the reactive pass. A type mismatch is
            // reported separately from an expression that yields nothing,
            // but neither result may leave an old computed value behind.
            //
            // Deliberately NOT gated on `affectedOids`: gating would skip
            // the clear when a form is first opened — the main path, and
            // the one this gate exists for. Only `hasValue` guards it,
            // because writing the empty
            // value over an already-empty field is a no-op semantically but
            // not mechanically: getDefaultItemValue returns a fresh `[]` for
            // Checkboxes every pass, which churns a re-render. See PR !40046
            // thread 290128.
            if (hasValue(context.formData[oid])) {
                const empty = getDefaultItemValue(itemMeta?.itemType);
                setValue(oid, empty);
                // Clinic refreshes __format(Decimals) BEFORE nulling a
                // machine-written number (quest.viewmodel.js assignValue),
                // so a clear must not leave the old value's format behind.
                setItemValue(oid, empty, configuredDecimals(itemMeta));
            }
            // Seeded unconditionally — OUTSIDE the guard above. A dependent
            // computed must read `undefined` here rather than a stored `[]`
            // or `null`, or it won't recognise "no value" and cascade its
            // own clear.
            context.formData = { ...context.formData, [oid]: undefined };
            continue;
        }

        // The gate passed, so any mismatch previously reported for this item
        // no longer holds. Fires whether or not there is a value: a clean
        // "computed nothing" is still not a type mismatch.
        resolveTypeMismatch(oid);

        if (normalized !== undefined) {
            // A Number item's designer decimals round the stored value
            // (22.642192… → 22.6 on a 1-decimal item) and travel as its
            // __format sidecar, like Clinic's assignValue. Non-Number
            // items pass through with no format (an undefined format is
            // the same as not passing one — the reducer skips the
            // sidecar).
            const { value: storedValue, format } = applyNumberPrecision(
                normalized,
                itemMeta
            );
            setValue(oid, storedValue);
            setItemValue(oid, storedValue, format);
            // Update the local context so chained expressions see fresh
            // values. A precision-rounded number is seeded as stored —
            // Clinic's chained expressions read the rounded value too —
            // while everything else keeps the raw result (e.g. a Date
            // stays a Date for downstream date arithmetic).
            context.formData = {
                ...context.formData,
                [oid]: format !== undefined ? storedValue : result,
            };
        } else if (hasValue(context.formData[oid])) {
            // Clear to the empty value instead of leaving a stale figure,
            // on both initial and reactive passes. Seed `undefined` (not
            // empty) locally so a dependent computed cascades its own
            // clear. The format is refreshed like Clinic does on clear —
            // see the mismatch clear above.
            const empty = getDefaultItemValue(itemMeta?.itemType);
            setValue(oid, empty);
            setItemValue(oid, empty, configuredDecimals(itemMeta));
            context.formData = { ...context.formData, [oid]: undefined };
        }
    }
}
